import React from 'react';

import { POSITION_TYPE_COLOURS, POSITION_TYPE_ORDER, positionTypeLabel } from 'Components/people/positionTypes';

import { formatSeparateByThousands } from 'Helpers/formatService';

/**
 * Workforce composition at the peak bucket — the type split, as a single
 * proportional bar plus a per-type legend.
 *
 * `standing_vs_surge` stays `null` until InBudget models a surge window. When it
 * is null nothing extra renders: an empty standing/surge frame would imply the
 * split exists and happens to be zero.
 */
export default function PeopleComposition({ composition }) {
    const byType = composition?.by_type;

    if (!byType) {
        return null;
    }

    const rows = POSITION_TYPE_ORDER.filter((type) => type in byType).map((type) => ({ type, count: byType[type] ?? 0 }));

    const total = rows.reduce((sum, row) => sum + row.count, 0);

    if (total <= 0) {
        return <p className="tw-text-sm tw-text-charcoal-50">{$t('No positions are active in the peak period.')}</p>;
    }

    return (
        <div>
            {!!composition.bucket_label && (
                <p className="tw-text-sm tw-text-charcoal-50 tw-mb-3">
                    {$t('Peak period: {{period}}', { period: composition.bucket_label })} ·{' '}
                    {$t('{{count}} positions', { count: formatSeparateByThousands(total) })}
                </p>
            )}

            <div className="tw-flex tw-w-full tw-h-4 tw-rounded tw-overflow-hidden" role="img" aria-label={$t('Position type split at peak')}>
                {rows
                    .filter((row) => row.count > 0)
                    .map((row) => (
                        <div
                            key={row.type}
                            title={`${positionTypeLabel(row.type)}: ${formatSeparateByThousands(row.count)}`}
                            style={{ width: `${(row.count / total) * 100}%`, background: POSITION_TYPE_COLOURS[row.type] }}
                        />
                    ))}
            </div>

            <dl className="tw-grid tw-grid-cols-2 md:tw-grid-cols-4 tw-gap-x-5 tw-gap-y-2 tw-mt-4 tw-mb-0">
                {rows.map((row) => (
                    <div key={row.type} className="tw-flex tw-items-baseline tw-gap-x-2">
                        <span
                            aria-hidden="true"
                            className="tw-inline-block tw-w-3 tw-h-3 tw-rounded-sm tw-shrink-0 tw-translate-y-0.5"
                            style={{ background: POSITION_TYPE_COLOURS[row.type] }}
                        />
                        <dt className="tw-text-sm tw-text-charcoal-75 tw-font-normal">{positionTypeLabel(row.type)}</dt>
                        <dd className="tw-text-sm tw-text-charcoal tw-font-semibold tw-mb-0">
                            {formatSeparateByThousands(row.count)}
                            <span className="tw-text-xs tw-text-charcoal-50 tw-font-normal tw-ms-1">({Math.round((row.count / total) * 100)}%)</span>
                        </dd>
                    </div>
                ))}
            </dl>
        </div>
    );
}
